import type { ApplicationInsights, INotificationListener, ITelemetryItem, } from "@microsoft/1ds-analytics-js";
import type { LogDatapointObserver } from "owa-analytics-types";
import { partitionEcsConfigIds } from "owa-analytics-utils";
import { trace } from "owa-trace";

interface InstanceObservations {
    pending: Map<string, Set<LogDatapointObserver>>;
    listener: INotificationListener;
}

const instances = new WeakMap<ApplicationInsights, InstanceObservations>();
const recentKeys = new WeakMap<ApplicationInsights, Map<string, number>>();

function correlationKey(item: ITelemetryItem): string | undefined {
    const data = item.data;
    if (typeof item.name !== "string" ||
        typeof item.time !== "string" ||
        item.time.length === 0 ||
        typeof data?.EventName !== "string" ||
        typeof data.SequenceNumber !== "number" ||
        !Number.isSafeInteger(data.SequenceNumber) ||
        data.SequenceNumber < 0 ||
        typeof data.OriginalThread !== "string" ||
        data.OriginalThread.length === 0) {
        return undefined;
    }
    return JSON.stringify([
        item.name,
        data.EventName,
        data.SequenceNumber,
        data.OriginalThread,
        item.time,
    ]);
}

/**
 * SDK "sent" includes fire-and-forget transports. It is not backend ingestion.
 * Existing scalar fields survive SDK root copying; object identity does not.
 */
export function observeSdkDatapoint(sdk: ApplicationInsights, item: ITelemetryItem, observer: LogDatapointObserver): void {
    if (!observer.isActive()) {
        return;
    }
    if (item.name !== "client_ecs_config_state") {
        observer.settle("suppressed");
        return;
    }
    const key = correlationKey(item);
    if (!key ||
        item.data?.EventName !== "EcsConfigStateStamping" ||
        typeof sdk.addNotificationListener !== "function" ||
        typeof sdk.removeNotificationListener !== "function") {
        observer.settle("unsupported");
        return;
    }
    let seen = recentKeys.get(sdk);
    if (!seen) {
        seen = new Map<string, number>();
        recentKeys.set(sdk, seen);
    }
    for (const [previousKey, deadline] of seen) {
        if (deadline <= observer.observation.createdAt) {
            seen.delete(previousKey);
        }
    }
    if (seen.has(key)) {
        const matches = instances.get(sdk)?.pending.get(key);
        for (const current of Array.from(matches ?? [])) {
            current.settle("unsupported");
        }
        observer.settle("unsupported");
        return;
    }
    // Keep a short tombstone after cleanup: cancelling one duplicate must not
    // make a late notification uniquely identify a different observation.
    seen.set(key, observer.observation.deadline);
    let instance = instances.get(sdk);
    if (!instance) {
        const pending = new Map<string, Set<LogDatapointObserver>>();
        const settleEvents = (events: ITelemetryItem[], outcome: "sdk-reported-sent" | "discarded" | "offline") => {
            for (const event of events) {
                const eventKey = correlationKey(event);
                const matches = eventKey ? pending.get(eventKey) : undefined;
                if (!matches) {
                    continue;
                }
                const ambiguous = matches.size !== 1;
                for (const current of Array.from(matches)) {
                    const mapping = current.observation.mapping;
                    const configIds = partitionEcsConfigIds(mapping.configIds);
                    const coherent = event.data?.EcsETag === mapping.etag &&
                        event.data?.EcsConfigIds === configIds.EcsConfigIds &&
                        event.data?.EcsDefaultConfigIds === configIds.EcsDefaultConfigIds;
                    current.settle(ambiguous || !coherent ? "unsupported" : outcome);
                }
            }
        };
        const listener: INotificationListener = {
            eventsSent: events => settleEvents(events, "sdk-reported-sent"),
            eventsDiscarded: events => settleEvents(events, "discarded"),
            offlineEventsStored: events => settleEvents(events, "offline"),
            unload: () => {
                recentKeys.delete(sdk);
                for (const observers of Array.from(pending.values())) {
                    for (const current of Array.from(observers)) {
                        current.settle("failed");
                    }
                }
            },
        };
        instance = { pending, listener };
        try {
            sdk.addNotificationListener(listener);
        }
        catch {
            observer.settle("unsupported");
            trace.warn("Config-state SDK notification registration failed.", "analytics");
            return;
        }
        instances.set(sdk, instance);
    }
    const owner = instance;
    const matches = owner.pending.get(key) ?? new Set<LogDatapointObserver>();
    matches.add(observer);
    owner.pending.set(key, matches);
    observer.addCleanup(() => {
        matches.delete(observer);
        if (matches.size === 0) {
            owner.pending.delete(key);
        }
        if (owner.pending.size === 0 && instances.get(sdk) === owner) {
            instances.delete(sdk);
            sdk.removeNotificationListener(owner.listener);
        }
    });
}
